'use client';

import {
  type OneTrustEnvironment,
  OneTrustProvider,
} from '@jouzen/onetrust-consent-manager';
import type { MiddlewareFunction } from '@segment/analytics-next';
import { useFeatureGate } from '@statsig/react-bindings';
import { env } from 'next-runtime-env';
import { type ReactNode, useEffect } from 'react';

import { hipaaMiddleware } from '@/analytics/plugins/hipaaPlugin';
import { debugAnalytics } from '@/utils/debugAnalytics';

import { HipaaCustomerIdSync } from './HipaaCustomerIdSync';

const HIPAA_MIDDLEWARE_GATE = 'enable_hipaa_middleware';

interface OneTrustEnvProps {
  environment?: OneTrustEnvironment;
  onetrustKey?: string;
  segmentKey?: string;
}

interface AnalyticsLike {
  _isOneTrustStub?: boolean;
  _oneTrustConsentManager?: boolean;
  instance?: unknown;
  addSourceMiddleware?: (fn: MiddlewareFunction) => unknown;
}

/**
 * Tracks which `AnalyticsBrowser` instances already have `hipaaMiddleware`, so
 * we never double-register the same instance across component remounts or React
 * StrictMode's double-invoked effects.
 *
 * Keyed by a `WeakSet` rather than a marker property on the instance,
 * since the Segment `AnalyticsBrowser` is a Proxy that does not reliably
 * echo back custom property writes.
 */
const instancesWithMiddleware = new WeakSet<object>();

function getMissingProps(props: OneTrustEnvProps): string[] {
  const missing: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) {
      missing.push(key);
    }
  }
  return missing;
}

const POLL_MS = 100;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes — covers slow consent flows.

/**
 * Registers `hipaaMiddleware` as a Segment source middleware on the loaded
 * `AnalyticsBrowser` (rather than the OneTrust pre-consent stub), and keeps it
 * registered across instance swaps.
 *
 * Why polling instead of the `segmentPlugins` prop?
 *
 * `@jouzen/onetrust-consent-manager` (<= 2.6.0) deadlocks when
 * `segmentPlugins` is non-empty: its load IIFE does
 * `await a.register(...plugins); await a.load(...)` while the
 * `AnalyticsBrowser` is still pre-init. `register()` returns a Promise that
 * only resolves once `_preInitBuffer` flushes — and that flush requires
 * `a.load(...)` to fire, which the await is blocking. The result is no
 * `cdn.segment.com` request and every event stuck in `_preInitBuffer`.
 *
 * Why not the `useAnalyticsReady()` hook? It flips true only AFTER the
 * consent manager has already flushed its stub queue, so early events would
 * miss the middleware. Polling lets us register the moment the swap from
 * stub to real `AnalyticsBrowser` happens — before the queue flush.
 *
 * Why keep polling after the first success?
 * OneTrust replaces `window.analytics` with a fresh `AnalyticsBrowser`
 * when consent resolves, so registering only once leaves the middleware on a
 * old instance while the live one emits events with NO HIPAA adjustments. So we
 * re-register whenever the instance identity changes, deduping per instance via
 * `instancesWithMiddleware`, until `POLL_TIMEOUT_MS`.
 */
function HipaaMiddlewareRegistrar() {
  const { value: enabled } = useFeatureGate(HIPAA_MIDDLEWARE_GATE);

  useEffect(() => {
    if (!enabled) return;
    let elapsed = 0;
    let registeredAtLeastOnce = false;

    // Registers the middleware on the current `window.analytics` when it is a
    // real, non-stub `AnalyticsBrowser` we have not registered on yet. Safe to
    // call repeatedly: a no-op once the live instance is already registered.
    const tryRegister = (): void => {
      const analytics = (window as unknown as { analytics?: AnalyticsLike })
        .analytics;
      if (
        !analytics ||
        analytics._isOneTrustStub ||
        typeof analytics.addSourceMiddleware !== 'function' ||
        instancesWithMiddleware.has(analytics)
      ) {
        return;
      }
      analytics.addSourceMiddleware(hipaaMiddleware);
      instancesWithMiddleware.add(analytics);
      registeredAtLeastOnce = true;
      debugAnalytics('HipaaMiddlewareRegistrar: registered', {
        data: { elapsedMs: elapsed },
        logToDatadog: 'HipaaMiddlewareRegistrar',
      });
    };

    tryRegister();

    const intervalId = window.setInterval(() => {
      elapsed += POLL_MS;
      tryRegister();
      if (elapsed >= POLL_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        // Only an error if we never registered at all. A successful
        // registration followed by watching for swaps is the normal path.
        if (!registeredAtLeastOnce) {
          debugAnalytics(
            'HipaaMiddlewareRegistrar: timed out waiting for analytics',
            {
              data: { elapsedMs: elapsed },
              level: 'error',
              logToDatadog: 'HipaaMiddlewareRegistrar',
            },
          );
        }
      }
    }, POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled]);

  return null;
}

const OneTrustConsentProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const envProps: OneTrustEnvProps = {
    environment: env('NEXT_PUBLIC_OURA_ENV') as OneTrustEnvironment,
    segmentKey: env('NEXT_PUBLIC_SEGMENT_KEY'),
    onetrustKey: env('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT'),
  };

  const missingProps = getMissingProps(envProps);
  if (missingProps.length > 0) {
    debugAnalytics('Missing required environment variables', {
      data: {
        missingProperties: missingProps.join(', '),
        component: 'OneTrustConsentProvider',
      },
      level: 'error',
      logToDatadog: 'OneTrustConsentProvider',
    });
    return <>{children}</>;
  }

  return (
    <OneTrustProvider
      applicationName="ecom-order-portal"
      environment={envProps.environment!}
      onetrustKey={envProps.onetrustKey!}
      segmentKey={envProps.segmentKey!}
      enableEventQueue={true}
      onScriptLoad={() => {
        const analytics = (
          globalThis as unknown as { analytics?: AnalyticsLike }
        ).analytics;
        // `OneTrust ready` is a successful lifecycle signal, not an error.
        // It is intentionally NOT reported to Datadog: it fires on every page
        // load and previously inflated the `web_eop_analytics.error` metric
        // (which drives the error alert). Console diagnostics are retained when debug analytics is enabled.
        debugAnalytics('OneTrust ready', {
          data: {
            component: 'OneTrustConsentProvider',
            hasAnalytics: Boolean(analytics),
            isOneTrustConsentManager: analytics?._oneTrustConsentManager,
            isOneTrustStub: analytics?._isOneTrustStub,
            hasInstance: Boolean(analytics?.instance),
          },
        });
      }}
      onScriptError={(error: Error) =>
        debugAnalytics('OneTrust error', {
          data: { error, component: 'OneTrustConsentProvider' },
          level: 'error',
          exception: true,
          logToDatadog: 'OneTrustConsentProvider',
        })
      }
    >
      <HipaaMiddlewareRegistrar />
      <HipaaCustomerIdSync />
      {children}
    </OneTrustProvider>
  );
};

export default OneTrustConsentProvider;
